/**
 * format.ts — the ONLY place that turns wire values into display strings.
 *
 * Money crosses the API as integer agorot (ARCHITECTURE §9); this module is the only place
 * that divides by 100. Number formatting follows spec-business §4/§8:
 *   `Intl.NumberFormat('he-IL')` on the ABSOLUTE value, then `₪` glued in front of the digits,
 *   then the sign (U+2212 MINUS / `+`) in front of the `₪` — never `style: 'currency'`.
 * Dates: `dd.mm.yyyy` in tables, drawers and dialogs; `dd.mm` in cards/chips when the year is
 * the current one and `dd.mm.yy` otherwise (spec-business §4 "מוסכמות לכל המסכים").
 * Chart axes use the mockup's K form ("0 / 10K / 20K", spec §6 "כללי").
 */

import { HE_MONTHS, HE_MONTHS_SHORT } from './i18n/he.ts';

const MINUS = '−'; // U+2212 MINUS SIGN — never a hyphen
const SHEKEL = '₪';

const nfCache = new Map<number, Intl.NumberFormat>();
function nf(decimals: number): Intl.NumberFormat {
  let f = nfCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('he-IL', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    nfCache.set(decimals, f);
  }
  return f;
}
const nf1max = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 1 });

/** Plain number with he-IL thousands separators; negatives carry U+2212. */
export function fmtNum(n: number, decimals = 0): string {
  const body = nf(decimals).format(Math.abs(n));
  return n < 0 ? MINUS + body : body;
}

/** `fmtMoney(123400)` → `₪1,234` · `fmtMoney(-41260, 2)` → `−₪412.60` · `plus` adds `+`. */
export function fmtMoney(agorot: number | null | undefined, decimals = 0, plus = false): string {
  if (agorot == null || !Number.isFinite(agorot)) return '—';
  const value = agorot / 100;
  const body = SHEKEL + nf(decimals).format(Math.abs(value));
  if (value < 0) return MINUS + body;
  if (plus && value > 0) return '+' + body;
  return body;
}

/** `≈ ₪9,747` — forecast figures and estimated tax events (spec §4.2/§4.4). */
export function fmtApprox(agorot: number | null | undefined, decimals = 0): string {
  if (agorot == null || !Number.isFinite(agorot)) return '—';
  return '≈ ' + fmtMoney(agorot, decimals);
}

/** Chart axis / column label: `0`, `10K`, `12.5K` (spec §6 "ציר y עם ערכים עגולים"). */
export function fmtK(agorot: number): string {
  const shekels = Math.round(agorot / 100);
  if (shekels === 0) return '0';
  const abs = Math.abs(shekels);
  const body = abs >= 1000 ? (abs % 1000 === 0 ? String(abs / 1000) + 'K' : nf1max.format(abs / 1000) + 'K') : nf(0).format(abs);
  return (shekels < 0 ? MINUS : '') + body;
}

/** Signed percentage: `+6%` / `−12%`. */
export function fmtPct(pct: number | null | undefined, plus = true, decimals = 0): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  const rounded = decimals ? Number(pct.toFixed(decimals)) : Math.round(pct);
  const sign = rounded < 0 ? MINUS : rounded > 0 && plus ? '+' : '';
  return sign + nf(decimals).format(Math.abs(rounded)) + '%';
}

/** Unsigned ratio as a percentage for meters: `33%`. */
export function fmtRatio(ratio: number | null | undefined, decimals = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return nf(decimals).format(ratio * 100) + '%';
}

/** A foreign original amount for the secondary line: `$25.00` / `EUR 12.00`. */
export function fmtForeign(minor: number | null | undefined, currency: string | null | undefined): string {
  if (minor == null || !currency) return '';
  const symbol = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency + ' ';
  return symbol + nf(2).format(Math.abs(minor) / 100);
}

/** `2026-09-06` → `06.09.2026` (tables, drawers, dialogs — BRIEF §5). */
export function fmtDate(day: string | null | undefined): string {
  if (!day) return '';
  const p = day.slice(0, 10).split('-');
  if (p.length !== 3) return day;
  return `${p[2]}.${p[1]}.${p[0]}`;
}

/**
 * `2026-09-06` → `06.09` in the current year, `06.09.2025` otherwise (spec §4 "בכרטיסים ושבבים").
 * `asOf` is the API's day — the client never derives "today" itself.
 *
 * Both branches are formats the docs allow and nothing else: REDESIGN §3's zero-padded `dd.mm`
 * (identical to `ddmm()` in `shared/insight/src/text.ts`) and BRIEF §5's `dd.mm.yyyy`. The old
 * unpadded two-digit-year form (`1.2.27`) was a third format and genuinely ambiguous beside
 * Hebrew text, so it is gone.
 */
export function fmtShortDate(day: string | null | undefined, asOf: string): string {
  if (!day) return '';
  const p = day.slice(0, 10).split('-');
  if (p.length !== 3) return day;
  const [year = '', month = '', dayOfMonth = ''] = p;
  return year === asOf.slice(0, 4) ? `${dayOfMonth}.${month}` : `${dayOfMonth}.${month}.${year}`;
}

/** ISO timestamp → `06.09.2026 06:12` in the viewer's time zone. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO timestamp → `08:40` (service-call times). */
export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const rtf = new Intl.RelativeTimeFormat('he', { numeric: 'auto' });

/** "לפני רגע" / "לפני 12 דק׳" / "לפני 5 שעות" / "לפני יומיים" — spec §2.3. */
export function relTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = (now.getTime() - then) / 1000;
  if (diff < 90) return 'לפני רגע';
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  return rtf.format(-Math.round(diff / 86400), 'day');
}

/** `2026-09` → `ספטמבר 2026` (or `ספטמבר` with `withYear = false`). */
export function monthName(month: string, withYear = true): string {
  const p = month.slice(0, 7).split('-');
  const idx = Number(p[1]) - 1;
  const name = HE_MONTHS[idx] ?? month;
  return withYear ? `${name} ${p[0]}` : name;
}

/** `2026-09` → `ספט׳ 26` (chart x-axis, spec §6 "ציר x חודשים בקיצור עברי"). */
export function monthShort(month: string, withYear = true): string {
  const p = month.slice(0, 7).split('-');
  const idx = Number(p[1]) - 1;
  const name = HE_MONTHS_SHORT[idx] ?? month;
  return withYear ? `${name} ${String(p[0]).slice(2)}` : name;
}

/** Avatar initial. */
export function initial(name: string): string {
  return (name || '?').trim().charAt(0);
}

/** `6751` → `***6751` (bank accounts) — rendered LTR-isolated by `<Mask>`. */
export function mask(last4: string | null | undefined): string {
  return last4 ? `***${last4}` : '';
}

/** `7712` → `****7712` (card PANs). */
export function cardMask(last4: string | null | undefined): string {
  return last4 ? `****${last4}` : '';
}
